import { env } from '@src/config/env';
import { reportNetworkError } from '@src/context/NetworkStatusContext';

/** Best-effort enrichment timeout — the caller falls back to the thin payload past this. */
export const ENRICHMENT_TIMEOUT_MS = 2500;

export interface EnrichedPunchBreak {
    breakId: number;
    in: string;
    out: string | null;
    paid: boolean;
}

export interface EnrichedPunch {
    clockedIn: string;
    clockedOut: string | null;
    roleId: number;
    externalRoleId: string | null;
    shiftId: number;
    departmentId: number;
    locationId: number;
    tips: number;
    userId: number;
    externalUserId: string | null;
    breaks: EnrichedPunchBreak[];
}

/**
 * Fetches a fully-resolved, curated time punch (authoritative punch data plus
 * server-resolved `externalRoleId`/`externalUserId`) from the punch enrichment
 * endpoint (API-4675). Mirrors `fetchAccount` / `fetchPunchMapping`: bearer
 * token, `json.data` unwrap, and network-failure reporting for offline
 * detection — except an intentional timeout-triggered abort is excluded from
 * that reporting (see `fetchWithTimeoutAwareNetworkReporting` below).
 *
 * Bounded by `ENRICHMENT_TIMEOUT_MS` — this is a best-effort call on the punch
 * success path, never allowed to hang the terminal. Rejects (with an
 * `AbortError`-like reason) on timeout; callers should catch and fall back to
 * the thin `punch_success` payload rather than propagate.
 */
export async function fetchEnrichedPunch(
    companyId: number,
    locationId: number,
    applicationName: string,
    timePunchId: number,
    accessToken: string
): Promise<EnrichedPunch> {
    const url = new URL(
        `${env.apiBaseUrl}/api/v2/company/${companyId}/integrations/${encodeURIComponent(applicationName)}/time_punches/${timePunchId}`
    );
    url.searchParams.set('location_id', String(locationId));

    // TS lib target predates the AbortSignal.timeout() static helper, so the
    // abort is wired up manually.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ENRICHMENT_TIMEOUT_MS);

    try {
        const res = await fetchWithTimeoutAwareNetworkReporting(url.toString(), {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json'
            },
            signal: controller.signal
        });

        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(
                `Punch enrichment fetch failed (${res.status}): ${body || res.statusText}`
            );
        }

        const json = await res.json();
        return json.data as EnrichedPunch;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Like `fetchWithNetworkReporting` (accountApi.ts), but distinguishes our own
 * intentional `ENRICHMENT_TIMEOUT_MS` abort from a genuine network failure.
 * A client-side timeout abort is expected best-effort behavior, not evidence
 * the terminal has gone offline — reporting it as a network error would flip
 * `NetworkStatusContext` to offline every time enrichment is merely slow,
 * even while the device has a perfectly healthy connection.
 */
async function fetchWithTimeoutAwareNetworkReporting(
    input: RequestInfo | URL,
    init?: RequestInit
): Promise<Response> {
    try {
        return await fetch(input, init);
    } catch (err) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
            reportNetworkError();
        }
        throw err;
    }
}
